import { useAuth, AccountModel } from "../utils/AuthContext.tsx";
import { Button } from "./ui/button.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import GoogleLoginButton from "./GoogleLoginButton.tsx";
import { IdTokenResult } from "firebase/auth";
import { KeyboardEvent, useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { AccountSwitcher } from "./AccountSwitcher.tsx";
import { SidebarProvider } from "./ui/sidebar.tsx";
import { Input } from "@/components/ui/input";

const authConfig = {
  apiKey: import.meta.env.VITE_GCIP_APIKEY,
  authDomain: import.meta.env.VITE_GCIP_DOMAIN,
};

const Login: React.FC = () => {
  const { login, logout, user, userAccounts, switchToAccount, getAccount } = useAuth();
  const { accountParam } = useParams();
  const navigate = useNavigate();
  const [selectedAccount, setSelectedAccount] = useState<AccountModel>();
  const [inputValue, setInputValue] = useState<string>("");
  const [accountError, setAccountError] = useState<boolean>(false);

  //an option from the list of accounts is selected
  const selectAccountOption = (slug: string | null): AccountModel | undefined => {
    let account = undefined;
    if (slug) {
      account = getAccount(slug);
      if (!account) {
        setInputValue(slug);
        setAccountError(true);
      }
    }
    setSelectedAccount(account);
    return account;
  };

  //selectedAccount has changed
  useEffect(() => {
    if (selectedAccount) {
      try {
        switchToAccount(selectedAccount); //update auth context
      } catch (error: any) {
        console.error("Could not switch to account:", error.message);
        logout(selectedAccount); //is this acting on selectedAccount?
      }
    }
  }, [selectedAccount]);

  //component has loaded
  useEffect(() => {
    //account specified via url param
    if (accountParam) {
      selectAccountOption(accountParam);
    }
  }, []);

  //attaches to a selected account
  const handleAccountSelection = (value: string) => {
    const account = selectAccountOption(value);
    if (account) {
      navigate(`/login/${account.slug}`);
    } else {
      setAccountError(true);
    }
  };

  //user has successfully completed login flow (via provider) and received a valid token
  const handleProviderLoginSuccess = (tokenResult: IdTokenResult) => {
    login(tokenResult.token); //pass token to auth context
    resumeNavigation(); //resume to desired url
  };

  //login failed or aborted by provider
  const handleProviderLoginFailure = () => {
    console.log("Login Failed.");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountError(false);
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAccountSelection(inputValue ?? "");
    }
  };

  //user has explcitly chosen to logout
  const handleLogout = () => {
    logout(selectedAccount ?? null); //update auth context
  };

  const resumeNavigation = () => {
    //check if user was attempting to access a specific path before login
    const resumePath = sessionStorage.getItem("resumePath");
    if (resumePath) navigate(resumePath);
    else navigate("/");
  };

  const handleResumeNavigation = () => {
    resumeNavigation();
  };

  let body = <></>;
  if (selectedAccount && user)
    body = (
      <div className="flex flex-col w-full max-w-[400px] z-10 gap-4">
        <Avatar className="rounded-md">
          <AvatarImage src={user?.picture} alt={user.email} />
          <AvatarFallback>?</AvatarFallback>
        </Avatar>
        <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl pt-4">Hi, {user.name}.</h1>
        <h2 className="text-xl text-muted-foreground pt-4">
          It looks like you're already logged into <strong>{selectedAccount.name}</strong> as {user.email}.
        </h2>
        <div className="flex justify-start py-8 gap-4">
          <Button onClick={handleResumeNavigation}>Resume</Button>
          <Button onClick={handleLogout} variant="ghost">
            Log Out
          </Button>
        </div>
      </div>
    );
  else {
    body = (
      <div className="max-w-full md:max-w-[400px] z-10">
        <h1 className="scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl text-accent-foreground">Fieldbook</h1>

        <div className="pt-8">
          {!selectedAccount && (
            <>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter an account (e.g., acme-123)"
                  className={`h-12 md:text-lg border-2 w-full md:w-[324px] ${
                    accountError ? "border-red-400 text-red-400" : "border-solid text-base"
                  }`}
                  onKeyDown={handleInputKeyDown}
                  onChange={handleInputChange}
                  value={inputValue}
                />
                <Button variant="default" className="h-12 w-12" onClick={() => handleAccountSelection(inputValue)}>
                  <ArrowRight className="!h-6 !w-6" />
                </Button>
              </div>
              {accountError && (
                <div className="text-sm text-red-400 py-2">Sorry, but "{inputValue}" isn't a recognized account.</div>
              )}
              <div className="text-muted-foreground flex gap-1 items-center justify-start py-1">
                {userAccounts.length > 0 && <span className="text-sm">Recently used:</span>}
                {userAccounts.map((a, i) => (
                  <Button
                    variant="link"
                    className="text-muted-foreground px-0"
                    onClick={() => handleAccountSelection(a.slug)}
                    key={i}
                  >
                    {a.name}
                    {i < userAccounts.length - 1 ? "," : ""}
                  </Button>
                ))}
              </div>
            </>
          )}
          {selectedAccount && (
            <>
              <h2 className="text-xl text-muted-foreground pt-4">
                Sign in using your <strong>{selectedAccount.name}</strong> account.
              </h2>
              <div className="flex justify-start py-8">
                <GoogleLoginButton
                  onSuccess={handleProviderLoginSuccess}
                  onError={handleProviderLoginFailure}
                  config={authConfig}
                  tenantId={selectedAccount.id}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-start h-screen px-8 md:px-32 lg:px-48">
      {body}
      {selectedAccount && (
        <SidebarProvider defaultOpen={true} className="w-64 absolute top-6 left-6 md:left-8 z-0">
          <AccountSwitcher
            side="bottom"
            onSuccess={(account: AccountModel) => {
              setSelectedAccount(account);
              navigate(`/login/${account.slug}`);
            }}
            onFailure={(account: AccountModel) => {
              setSelectedAccount(account);
              navigate(`/login/${account.slug}`);
            }}
            onAdd={() => {
              setSelectedAccount(undefined);
              navigate("/login");
            }}
          />
        </SidebarProvider>
      )}

      <h3 className="text-xs text-muted-foreground fixed bottom-8 right-8">
        Console v0.1.{import.meta.env.VITE_BUILD}
      </h3>
    </div>
  );
};

export default Login;
